import { useState, useEffect } from 'react';
import { Typography, Spin, Alert, Button } from 'antd';
import ChatInterfaceWithHistory from '../../components/ChatInterfaceWithHistory';
import NewConversationDialog from '../../components/NewConversationDialog';
import GemContextModal from '../../components/GemContextModal';
import {
  getConfigsByType,
  createConversation,
  getLastGemContext,
} from '../../api/chatbot/ChatbotApi';

const { Title, Text } = Typography;

const GemChatPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gemConfig, setGemConfig] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showNewConvoDialog, setShowNewConvoDialog] = useState(false);
  const [showContextModal, setShowContextModal] = useState(false);
  const [lastContext, setLastContext] = useState(null);
  const [contextForModal, setContextForModal] = useState(null);
  const [initialMessageData, setInitialMessageData] = useState(null);
  const [highlightNewConversation, setHighlightNewConversation] = useState(false);

  useEffect(() => {
    // Clear any old cached conversation data that might have AYD prefix
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.includes('conv_ayd_') || key.includes('ayd')) {
        localStorage.removeItem(key);
      }
    });
    
    loadGemConfig();
  }, []);

  const loadGemConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const configs = await getConfigsByType('GEM');
      
      if (configs && configs.length > 0) {
        setGemConfig(configs[0]);
        
        // Load last context
        try {
          const context = await getLastGemContext(configs[0].id);
          setLastContext(context);
        } catch (contextErr) {
          // Don't fail if context loading fails
        }
      } else {
        setError('GEM is not configured. Please go to Settings > General > Chatbot and create a GEM configuration.');
      }
    } catch (err) {
      console.error('Error loading GEM config:', err);
      setError(`Failed to load GEM configuration: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNewConversation = () => {
    setShowNewConvoDialog(true);
  };

  const handleConversationOptionSelect = async (option) => {
    setShowNewConvoDialog(false);

    if (option === 'specify') {
      setContextForModal(null);
      setShowContextModal(true);
    } else if (option === 'usePrevious') {
      setContextForModal(lastContext);
      setShowContextModal(true);
    } else if (option === 'chat') {
      // Create conversation without context
      try {
        const result = await createConversation(gemConfig.id, null);
        
        if (result && result.conversationId) {
          setCurrentConversationId(result.conversationId);
        } else {
          throw new Error('No conversation ID returned');
        }
      } catch (err) {
        console.error('Error creating conversation:', err);
        setError(`Failed to create conversation: ${err.message}`);
      }
    }
  };

  const handleContextSubmit = async (context) => {
    try {
      const result = await createConversation(gemConfig.id, context);
      
      if (result && result.conversationId) {
        setLastContext(context);
        
        // IMMEDIATELY set the conversation ID to show the chat interface
        setCurrentConversationId(result.conversationId);
        
        // Highlight the new conversation
        setHighlightNewConversation(true);
        
        // IMMEDIATELY reload conversations to show the new one in the list
        loadGemConfig();
        
        // Pass the initial message data to the chat interface
        setInitialMessageData({
          conversationId: result.conversationId,
          context: context,
          message: buildInitialMessage(context)
        });
        
        // Clear any cached conversation data
        localStorage.removeItem('lastConversationId');
        
        // Remove highlight after 5 seconds
        setTimeout(() => {
          setHighlightNewConversation(false);
        }, 3000);
      } else {
        throw new Error('No conversation ID returned');
      }
    } catch (err) {
      console.error('Error creating conversation with context:', err);
      setError(`Failed to create conversation: ${err.message}`);
    }
  };



  const buildInitialMessage = (context) => {
    // Build a coherent message from the context
    const parts = [];
    
    if (context.objective) {
      parts.push(`Strategic Objective: ${context.objective}`);
    }
    if (context.companyName) {
      parts.push(`Company Name: ${context.companyName}`);
    }
    if (context.industry) {
      parts.push(`Industry: ${context.industry}`);
    }
    if (context.country) {
      parts.push(`Country: ${context.country}`);
    }
    if (context.companySize) {
      parts.push(`Company Size: ${context.companySize}`);
    }
    if (context.timeFrame) {
      parts.push(`Time Frame: ${context.timeFrame}`);
    }
    if (context.challenges) {
      parts.push(`Current Challenges: ${context.challenges}`);
    }
    if (context.additionalContext) {
      parts.push(`Additional Context: ${context.additionalContext}`);
    }
    
    // Build the message
    let message = parts.join('\n');
    if (message) {
      message += '\n\nBased on these requirements, what strategies would you recommend to achieve this objective?';
    } else {
      message = 'What strategies would you recommend for achieving strategic objectives?';
    }
    
    return message;
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Alert 
          message="Configuration Error" 
          description={error} 
          type="error" 
          showIcon 
          action={
            <Button size="small" onClick={loadGemConfig}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflow: 'hidden', background: '#0d1b2a', display: 'flex', flexDirection: 'column' }}>
      {/* Override the dashboard layout scroll container so GEM fills it without page scroll */}
      <style>{`
        .site-layout > div:last-of-type {
          overflow: hidden !important;
        }
      `}</style>
      {gemConfig && (
        <ChatInterfaceWithHistory
          configId={gemConfig.id}
          conversationId={currentConversationId}
          onConversationChange={setCurrentConversationId}
          botType="GEM"
          onNewChat={handleNewConversation}
          initialMessageData={initialMessageData}
          onInitialMessageSent={() => setInitialMessageData(null)}
          highlightNewConversation={highlightNewConversation}
        />
      )}

      <NewConversationDialog
        open={showNewConvoDialog}
        onClose={() => setShowNewConvoDialog(false)}
        onSelectOption={handleConversationOptionSelect}
        hasLastContext={!!lastContext}
      />

      <GemContextModal
        open={showContextModal}
        onClose={() => setShowContextModal(false)}
        onSubmit={handleContextSubmit}
        initialContext={contextForModal}
      />
    </div>
  );
};

export default GemChatPage;
